import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, BadgeCheck, Pencil } from "lucide-react";
import { fetchProfile, trackProfileView } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ReviewSection from "@/components/ReviewSection";
import VideoSection from "@/components/VideoSection";
import SocialLinks from "@/components/SocialLinks";

export default function ProfileDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchProfile(id).then(setP).catch(() => setNotFound(true));
    trackProfileView(id);
  }, [id]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <p className="font-serif italic text-3xl text-sand-700">This profile is off the menu.</p>
        <Link to="/directory" className="mt-6 inline-block font-mono text-[11px] tracking-[0.25em] uppercase text-brand-500 editorial-link">
          Back to Directory →
        </Link>
      </div>
    );
  }
  if (!p) {
    return <div className="max-w-3xl mx-auto px-6 py-32 font-mono text-sm text-sand-600">Loading…</div>;
  }

  const isOwner = user && user.user_id === p.owner_user_id;

  return (
    <div data-testid="profile-detail-page" className="bg-sand-50">
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-20">
        <Link to="/directory" className="font-mono text-[10px] tracking-[0.25em] uppercase text-sand-600 editorial-link">
          ← The Directory
        </Link>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="img-zoom aspect-[4/5] bg-sand-100">
              {p.cover_image ? (
                <img src={p.cover_image} alt={p.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-sand-200">
                  <span className="font-serif text-7xl text-sand-300">{p.name?.[0]}</span>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-forest-500 text-sand-50 px-3 py-1 font-mono text-[10px] tracking-[0.2em] uppercase">
                {p.type}
              </span>
              {p.verified && (
                <span className="bg-brand-500 text-sand-50 px-3 py-1 font-mono text-[10px] tracking-[0.2em] uppercase flex items-center gap-1">
                  <BadgeCheck size={12} /> Verified
                </span>
              )}
            </div>
            <h1 className="font-serif font-black text-5xl md:text-6xl leading-[0.98] text-sand-900">{p.name}</h1>
            <div className="mt-4 flex items-center gap-1.5 text-sand-700">
              <MapPin size={15} />
              <span>{p.neighborhood ? `${p.neighborhood} · ` : ""}{p.city}</span>
            </div>
            {p.bio && <p className="mt-6 text-lg font-light leading-relaxed text-sand-700">{p.bio}</p>}

            {p.signature_items?.length > 0 && (
              <div className="mt-8">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-brand-500 mb-3">Signature</div>
                <ul className="space-y-1.5">
                  {p.signature_items.map((s) => (
                    <li key={s} className="font-serif text-xl text-sand-900 border-b border-sand-200 pb-1.5">{s}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-8">
              <SocialLinks item={p} />
            </div>

            {isOwner && (
              <Link
                to={`/profile/${p.id}/edit`}
                data-testid="profile-detail-edit"
                className="mt-10 inline-flex items-center gap-2 bg-sand-900 text-sand-50 px-6 py-3 font-mono text-[11px] font-bold tracking-[0.2em] uppercase hover:bg-brand-500 transition-colors"
              >
                <Pencil size={14} /> Edit Profile
              </Link>
            )}
          </div>
        </div>

        {p.gallery?.length > 0 && (
          <div className="mt-16 grid grid-cols-2 md:grid-cols-3 gap-4">
            {p.gallery.map((g, i) => (
              <div key={i} className="img-zoom aspect-square bg-sand-100">
                <img src={g} alt={`${p.name} ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        )}
      </section>
      <VideoSection entityType="profile" entityId={id} isOwner={isOwner} />
      <ReviewSection entityType="profile" entityId={id} />
    </div>
  );
}
